import {
  CalendarDays,
  Download,
  IndianRupee,
  Percent,
} from "lucide-react";

export type TaxTdsRecord = {
  id: string;
  quarter: string;
  quarterCode: string;
  year: number;
  totalReferralIncentives: number;
  tdsPercentage: number;
  tdsAmount: number;
  certificateName: string;
};

interface Props {
  records: TaxTdsRecord[];
  resultStart: number;
  loading?: boolean;
  error?: string | null;
  onDownload: (record: TaxTdsRecord) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const TaxTable = ({
  records,
  resultStart,
  loading,
  error,
  onDownload,
}: Props) => {
  return (
    <div className="tax-tds-table-card">
      <div
        className="tax-tds-table-scroll"
        role="region"
        aria-label="Tax TDS table"
        tabIndex={0}
      >
        <table className="tax-tds-table">
          <colgroup>
            <col className="tax-col-sno" />
            <col className="tax-col-quarter" />
            <col className="tax-col-referral" />
            <col className="tax-col-tds" />
            <col className="tax-col-certificate" />
          </colgroup>

          <thead>
            <tr>
              <th>S.NO</th>
              <th>QUARTER</th>
              <th>TOTAL REFERRAL INCENTIVES</th>
              <th>TDS AMOUNT</th>
              <th>TDS CERTIFICATE</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="tax-tds-empty">
                  Loading Tax - TDS report...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="tax-tds-empty">
                  {error}
                </td>
              </tr>
            ) : records.length > 0 ? (
              records.map((record, index) => (
                <tr key={record.id}>
                  <td>
                    <span className="tax-tds-serial">
                      {resultStart + index}
                    </span>
                  </td>

                  <td>
                    <div className="tax-tds-quarter">
                      <CalendarDays size={17} strokeWidth={2.2} />
                      <div>
                        <strong>
                          {record.quarterCode} {record.year}
                        </strong>
                        <span>{record.quarter}</span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="tax-tds-money">
                      <span className="tax-tds-money-icon">
                        <IndianRupee size={17} strokeWidth={2.45} />
                      </span>
                      <div>
                        <strong>
                          {formatCurrency(
                            record.totalReferralIncentives,
                          )}
                        </strong>
                        <span>Referral incentives</span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="tax-tds-money tax-tds-money-tax">
                      <span className="tax-tds-money-icon tax-tds-money-icon-tax">
                        <Percent size={17} strokeWidth={2.4} />
                      </span>
                      <div>
                        <strong>
                          {formatCurrency(record.tdsAmount)}
                        </strong>
                        <span>
                          {record.tdsPercentage}% TDS deducted
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <button
                      type="button"
                      className="tax-tds-download"
                      onClick={() => onDownload(record)}
                    >
                      <Download size={16} strokeWidth={2.3} />
                      <span>Download</span>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="tax-tds-empty">
                  No Tax - TDS records found for the selected
                  filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaxTable;
